import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle2, Sparkles } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import promotionsService from '../../services/promotionsService';
import { Promotion, PromotionField, PromotionSurface, SubmitLeadPayload } from '../../types/promotion';
import { getTenantConfig } from '../../config/tenantConfig';

interface LeadCaptureModalProps {
  open: boolean;
  onClose: () => void;
  promotion: Promotion;
  source: PromotionSurface;
  basketId?: string;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const normalisePhone = (raw: string): string | null => {
  const digits = (raw || '').replace(/\D/g, '');
  let d = digits;
  if (d.startsWith('91') && d.length === 12) d = d.slice(2);
  else if (d.startsWith('0') && d.length === 11) d = d.slice(1);
  return d.length === 10 ? d : null;
};

function validateField(field: PromotionField, raw: string): string | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) {
    return field.required ? `${field.label} is required` : null;
  }
  if (field.max_length && trimmed.length > field.max_length) {
    return `${field.label} is too long (max ${field.max_length})`;
  }
  if (field.type === 'email' && !EMAIL_RE.test(trimmed.toLowerCase())) {
    return `Enter a valid ${field.label.toLowerCase()}`;
  }
  if (field.type === 'tel' && !normalisePhone(trimmed)) {
    return `Enter a 10-digit ${field.label.toLowerCase()}`;
  }
  if (field.regex && !new RegExp(field.regex).test(trimmed)) {
    return `${field.label} format is invalid`;
  }
  return null;
}

const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({
  open, onClose, promotion, source, basketId,
}) => {
  const initialValues = useMemo<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const f of promotion.fields) v[f.name] = '';
    return v;
  }, [promotion.fields]);

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setValues(initialValues);
      setErrors({});
      setSubmitError(null);
      setSubmitting(false);
      setSubmitted(false);
    }
  }, [open, initialValues]);

  useEffect(() => {
    if (!submitted) return;
    const t = setTimeout(onClose, 2000);
    return () => clearTimeout(t);
  }, [submitted, onClose]);

  const updateField = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateAll = (): boolean => {
    const next: Record<string, string> = {};
    for (const field of promotion.fields) {
      const err = validateField(field, values[field.name] || '');
      if (err) next[field.name] = err;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validateAll()) return;

    setSubmitting(true);
    try {
      const payload: SubmitLeadPayload = {
        ...values,
        source,
        tenant: getTenantConfig().id,
      };
      // Email type: lowercase. Phone type: normalise.
      for (const field of promotion.fields) {
        const v = values[field.name] || '';
        if (field.type === 'email') payload[field.name] = v.trim().toLowerCase();
        else if (field.type === 'tel') payload[field.name] = normalisePhone(v) || v;
        else payload[field.name] = v.trim();
      }
      if (basketId && (source === 'marketplace-card' || source === 'marketplace-detail')) {
        payload.basket_id = basketId;
      }
      await promotionsService.submitLead(promotion.promo_id, payload);
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const firstName = useMemo(() => {
    // Use whichever field is the most-likely "name" for the success greeting.
    const nameField = promotion.fields.find((f) => /name/i.test(f.name));
    if (!nameField) return '';
    return (values[nameField.name] || '').trim().split(' ')[0];
  }, [promotion.fields, values]);

  return (
    <Modal isOpen={open} onClose={submitting ? () => {} : onClose} maxWidth="md" ariaLabel={promotion.title}>
      <div className="relative px-6 pt-6 pb-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 text-zebu-600 dark:text-zebu-400">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs font-medium uppercase tracking-wider">Early Access</span>
        </div>
        <h2 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">{promotion.title}</h2>
        {promotion.subtitle && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{promotion.subtitle}</p>
        )}
      </div>

      {submitted ? (
        <div className="px-6 pb-8 pt-4">
          <div className="flex flex-col items-center text-center py-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-3" />
            <div className="text-base font-medium text-gray-900 dark:text-white">
              {firstName ? `Thanks, ${firstName}!` : 'Thanks!'}
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {promotion.success_text}
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="px-6 pt-4 pb-6 space-y-4">
          {promotion.fields.map((field, idx) => (
            <div key={field.name}>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                {field.label}
              </label>
              <Input
                type={field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : 'text'}
                value={values[field.name] || ''}
                onChange={(e) => updateField(field.name, e.target.value)}
                placeholder={
                  field.type === 'email' ? 'you@example.com'
                    : field.type === 'tel' ? '10-digit mobile number'
                    : field.label
                }
                autoComplete={
                  field.type === 'email' ? 'email'
                    : field.type === 'tel' ? 'tel'
                    : /name/i.test(field.name) ? 'name' : undefined
                }
                inputMode={field.type === 'tel' ? 'numeric' : undefined}
                error={errors[field.name]}
                disabled={submitting}
              />
            </div>
          ))}

          {submitError && (
            <div className="text-sm text-red-600 dark:text-red-400">{submitError}</div>
          )}

          <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={submitting}>
            {submitting ? 'Submitting…' : promotion.cta_text}
          </Button>

          {promotion.consent_text && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              {promotion.consent_text}
            </p>
          )}
        </form>
      )}
    </Modal>
  );
};

export default LeadCaptureModal;
